/**
 * Department runtime API — typed wrappers + TanStack Query cache keys.
 *
 * Mirrors apps/chaos-gateway/src/routes/departments.ts. Real-time
 * updates land via `useDepartmentEvents` (lib/departments/use-department-events.ts).
 */
import type {
  DepartmentDetail,
  DepartmentSummaryList,
  Escalation,
  EscalationRequest,
  Handoff,
  HandoffContract,
  HandoffRequest,
  QueueItemStatus,
  QueuePage,
} from '@chaos/types/department';

import { apiJson } from './client';

interface QueueListParams {
  limit?: number;
  offset?: number;
  status?: QueueItemStatus;
}

export const departmentsApi = {
  list(): Promise<DepartmentSummaryList> {
    return apiJson<DepartmentSummaryList>('/departments');
  },

  get(deptKey: string): Promise<DepartmentDetail> {
    return apiJson<DepartmentDetail>(`/departments/${deptKey}`);
  },

  listQueue(deptKey: string, params?: QueueListParams): Promise<QueuePage> {
    const search = new URLSearchParams();
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    if (params?.offset !== undefined) search.set('offset', String(params.offset));
    if (params?.status !== undefined) search.set('status', params.status);
    const suffix = search.toString();
    return apiJson<QueuePage>(`/departments/${deptKey}/queue${suffix ? `?${suffix}` : ''}`);
  },

  listHandoffContracts(): Promise<HandoffContract[]> {
    return apiJson<HandoffContract[]>('/handoffs/contracts');
  },

  submitHandoff(input: HandoffRequest): Promise<Handoff> {
    return apiJson<Handoff>('/handoffs', { method: 'POST', json: input });
  },

  getHandoff(handoffId: string): Promise<Handoff> {
    return apiJson<Handoff>(`/handoffs/${handoffId}`);
  },

  openEscalation(input: EscalationRequest): Promise<Escalation> {
    return apiJson<Escalation>('/escalations', { method: 'POST', json: input });
  },

  getEscalation(escalationId: string): Promise<Escalation> {
    return apiJson<Escalation>(`/escalations/${escalationId}`);
  },
};

export const departmentsKeys = {
  all: ['departments'] as const,
  list: () => [...departmentsKeys.all, 'list'] as const,
  detail: (deptKey: string) => [...departmentsKeys.all, 'detail', deptKey] as const,
  queue: (deptKey: string, params?: QueueListParams) =>
    [...departmentsKeys.all, 'queue', deptKey, params ?? null] as const,
  handoffContracts: () => [...departmentsKeys.all, 'handoff-contracts'] as const,
  handoff: (handoffId: string) => [...departmentsKeys.all, 'handoff', handoffId] as const,
  escalation: (id: string) => [...departmentsKeys.all, 'escalation', id] as const,
};
